import { useState, useEffect, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { FaLock, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import { ThemeContext } from '../App';

export default function ResetPassword() {
  const { darkMode } = useContext(ThemeContext);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  console.log('🔐 ResetPassword montado');

  useEffect(() => {
    const processRecoveryLink = async () => {
      // Lê parâmetros tanto do hash (#) quanto da query string (?)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const searchParams = new URLSearchParams(window.location.search);

      const type = hashParams.get('type') || searchParams.get('type');
      const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
      const tokenHash = searchParams.get('token_hash'); // Supabase v2 PKCE flow
      
      console.log('🔍 ResetPassword - URL params:', { 
        type, 
        hasAccessToken: !!accessToken,
        hasTokenHash: !!tokenHash,
        fullHash: window.location.hash,
        search: window.location.search,
      });

      if (tokenHash) {
        // Fluxo PKCE (Supabase v2 padrão): troca o token_hash por uma sessão ativa
        setVerifying(true);
        try {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          if (verifyError) {
            console.error('❌ Erro ao verificar token de recuperação (PKCE):', verifyError);
            setError('Link de recuperação inválido ou expirado. Solicite um novo link.');
          } else {
            console.log('✅ Token PKCE verificado com sucesso - sessão de recuperação ativa');
          }
        } catch (e) {
          console.error('❌ Exceção ao verificar token:', e);
          setError('Erro ao processar o link de recuperação. Tente novamente.');
        } finally {
          setVerifying(false);
        }
      } else if (accessToken) {
        // Fluxo legado (implicit): o Supabase JS detecta o access_token automaticamente
        console.log('✅ Token de recuperação (implicit flow) detectado');
      } else {
        // Fluxo PKCE (Supabase v2 padrão): o SDK trocou o ?code= por sessão automaticamente.
        // Verifica se já existe uma sessão de recuperação ativa.
        setVerifying(true);
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session) {
            console.log('✅ Sessão de recuperação já ativa (PKCE code flow)');
          } else {
            console.error('❌ Link inválido - sem token_hash, access_token nem sessão ativa');
            setError('Link de recuperação inválido ou expirado. Solicite um novo link.');
          }
        } catch (e) {
          console.error('❌ Erro ao verificar sessão de recuperação:', e);
          setError('Erro ao processar o link de recuperação. Tente novamente.');
        } finally {
          setVerifying(false);
        }
      }
    };

    processRecoveryLink();
  }, []);

  const validatePassword = (password) => {
    return password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password);
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '#ccc' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Fraca', color: '#f44336' };
    if (strength <= 3) return { strength: 66, label: 'Média', color: '#ff9800' };
    return { strength: 100, label: 'Forte', color: '#4caf50' };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    // Validações
    if (!password) {
      setError('Digite uma nova senha');
      setLoading(false);
      return;
    }

    if (!validatePassword(password)) {
      setError('A senha deve ter pelo menos 8 caracteres, incluindo uma letra maiúscula e um número');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não conferem');
      setLoading(false);
      return;
    }

    // Atualizar senha
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      setError('Erro ao redefinir senha. Tente novamente.');
      console.error('Reset password error:', error);
    } else {
      setSuccess(true);
      setMessage('Senha redefinida com sucesso! Redirecionando...');
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    }
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: darkMode ? 'linear-gradient(135deg, #232526 0%, #414345 100%)' : 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
        padding: '20px'
      }}>>
        <div style={{
          background: darkMode ? '#232526' : 'white',
          borderRadius: 18,
          boxShadow: darkMode ? '0 4px 24px #1118' : '0 4px 24px rgba(25, 118, 210, 0.13)',
          padding: '3rem 2rem',
          width: '100%',
          maxWidth: 420,
          textAlign: 'center'
        }}>
          <div style={{
            width: 80,
            height: 80,
            background: '#4caf50',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <FaCheck style={{ fontSize: 40, color: 'white' }} />
          </div>
          <h2 style={{ color: darkMode ? '#90caf9' : '#1976d2', marginBottom: 12 }}>
            Senha Redefinida!
          </h2>
          <p style={{ color: darkMode ? '#aaa' : '#666', marginBottom: 24 }}>
            Sua senha foi atualizada com sucesso. Você será redirecionado para a página de login.
          </p>
        </div>
      </div>
    );
  }

  if (verifying) {
    return (
      <div style={{
        minHeight: '100vh',
        background: darkMode ? 'linear-gradient(135deg, #232526 0%, #414345 100%)' : 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      }}>
        <div style={{ textAlign: 'center', color: darkMode ? '#90caf9' : '#1976d2' }}>
          <p style={{ fontSize: 16, fontWeight: 500 }}>Verificando link de recuperação...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: darkMode ? 'linear-gradient(135deg, #232526 0%, #414345 100%)' : 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        background: darkMode ? '#232526' : 'white',
        borderRadius: 18,
        boxShadow: darkMode ? '0 4px 24px #1118' : '0 4px 24px rgba(25, 118, 210, 0.13)',
        padding: '2.5rem 2rem',
        width: '100%',
        maxWidth: 420
      }}>
        <img src="/logo.png" alt="Logo" style={{ 
          width: 120, 
          marginBottom: 18, 
          borderRadius: 12, 
          boxShadow: darkMode ? '0 2px 8px #1118' : '0 2px 8px #e0eafc',
          display: 'block',
          margin: '0 auto 24px'
        }} />
        
        <h2 style={{ 
          color: darkMode ? '#90caf9' : '#1976d2', 
          fontWeight: 700, 
          marginBottom: 8,
          textAlign: 'center'
        }}>
          Redefinir Senha
        </h2>
        
        <p style={{ 
          color: darkMode ? '#aaa' : '#666', 
          fontSize: 14, 
          marginBottom: 24,
          textAlign: 'center'
        }}>
          Digite sua nova senha abaixo
        </p>

        <form onSubmit={handleResetPassword}>
          {/* Campo Nova Senha */}
          <div style={{ position: 'relative', marginBottom: 8, width: '100%' }}>
            <FaLock style={{ 
              position: 'absolute', 
              left: 12, 
              top: 13, 
              color: darkMode ? '#90caf9' : '#1976d2', 
              fontSize: 18 
            }} />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Nova Senha"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                padding: '12px 40px 12px 38px',
                borderRadius: 8,
                border: darkMode ? '1.5px solid #90caf9' : '1.5px solid #1976d2',
                width: '100%',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box',
                background: darkMode ? '#232526' : '#f8fafc',
                color: darkMode ? '#f8fafc' : undefined,
              }}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: 12,
                top: 13,
                background: 'none',
                border: 'none',
                color: darkMode ? '#90caf9' : '#1976d2',
                cursor: 'pointer',
                fontSize: 18
              }}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* Indicador de força da senha */}
          {password && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                width: '100%',
                height: 6,
                background: '#e0e0e0',
                borderRadius: 3,
                overflow: 'hidden',
                marginBottom: 4
              }}>
                <div style={{
                  width: `${passwordStrength.strength}%`,
                  height: '100%',
                  background: passwordStrength.color,
                  transition: 'all 0.3s'
                }} />
              </div>
              <div style={{ fontSize: 12, color: passwordStrength.color, fontWeight: 600 }}>
                Senha {passwordStrength.label}
              </div>
            </div>
          )}

          {/* Campo Confirmar Senha */}
          <div style={{ position: 'relative', marginBottom: 24, width: '100%' }}>
            <FaLock style={{ 
              position: 'absolute', 
              left: 12, 
              top: 13, 
              color: darkMode ? '#90caf9' : '#1976d2', 
              fontSize: 18 
            }} />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmar Nova Senha"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              style={{
                padding: '12px 40px 12px 38px',
                borderRadius: 8,
                border: darkMode ? '1.5px solid #90caf9' : '1.5px solid #1976d2',
                width: '100%',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box',
                background: darkMode ? '#232526' : '#f8fafc',
                color: darkMode ? '#f8fafc' : undefined,
              }}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: 'absolute',
                right: 12,
                top: 13,
                background: 'none',
                border: 'none',
                color: darkMode ? '#90caf9' : '#1976d2',
                cursor: 'pointer',
                fontSize: 18
              }}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: darkMode ? '#90caf9' : '#1976d2',
              color: darkMode ? '#232526' : 'white',
              border: 'none',
              borderRadius: 8,
              padding: '12px 0',
              fontSize: 16,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: darkMode ? '0 2px 8px #1118' : '0 2px 8px #e0eafc',
            }}
          >
            {loading ? 'Redefinindo...' : 'Redefinir Senha'}
          </button>

          {error && (
            <p style={{ marginTop: 18, color: '#d32f2f', fontWeight: 500, textAlign: 'center' }}>
              {error}
            </p>
          )}
          {message && (
            <p style={{ marginTop: 18, color: '#4caf50', fontWeight: 500, textAlign: 'center' }}>
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
